import { Box } from "@mui/material";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { createRef, useEffect, useRef, type RefObject } from "react";
import { Toaster } from "react-hot-toast";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import Home from "./pages/Home";
import Angebot from "./pages/AngebotV2";
import Kontakt from "./pages/Kontakt";
import Leistungen from "./pages/Leistungen";
import ServiceDetail from "./pages/ServiceDetail";
import TerminVereinbaren from "./pages/TerminVereinbaren";
import UeberUns from "./pages/UeberUns";
import Referenzen from "./pages/Referenzen";
import HaeufigeFragen from "./pages/HaeufigeFragen";
import Datenschutz from "./pages/Datenschutz";
import Impressum from "./pages/Impressum";
import NachrichtGesendet from "./pages/NachrichtGesendet";
import NotFound from "./pages/NotFound";
import Navigation from "./components/Navigation";
import { initializeCookieConsent } from "./cookieConsent";

function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [hash, pathname]);

  return null;
}

import Footer from "./components/Footer";

function AnimatedRoutes() {
  const location = useLocation();
  const transitionRefs = useRef<Record<string, RefObject<HTMLDivElement>>>({});
  if (!transitionRefs.current[location.pathname]) {
    transitionRefs.current[location.pathname] = createRef<HTMLDivElement>();
  }
  const nodeRef = transitionRefs.current[location.pathname];

  return (
    <Box component="main" sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <SwitchTransition mode="out-in">
        <CSSTransition key={location.pathname} classNames="page" timeout={300} unmountOnExit nodeRef={nodeRef}>
          <Box ref={nodeRef} sx={{ flex: 1 }}>
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/angebot" element={<Angebot />} />
              <Route path="/haeufige-fragen" element={<HaeufigeFragen />} />
              <Route path="/faq" element={<Navigate to="/haeufige-fragen" replace />} />
              <Route path="/referenzen" element={<Referenzen />} />
              <Route path="/kundenbewertungen" element={<Navigate to="/referenzen" replace />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/leistungen" element={<Leistungen />} />
              <Route path="/leistungen/:slug" element={<ServiceDetail />} />
              <Route path="/termin-vereinbaren" element={<TerminVereinbaren />} />
              <Route path="/nachricht-gesendet" element={<NachrichtGesendet />} />
              <Route path="/ueber-uns" element={<UeberUns />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Box>
        </CSSTransition>
      </SwitchTransition>
    </Box>
  );
}

export default function App() {
  useEffect(() => {
    void initializeCookieConsent();
  }, []);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ScrollToHash />
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Toaster position="top-right" />
        <Navigation />
        <AnimatedRoutes />
        <Footer />
      </Box>
    </BrowserRouter>
  );
}
